/** Pure helpers for Web Speech result folding (Chrome re-sends finals). */

function normalizeUtterance(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

/**
 * Merge one utterance onto another without concatenating Chrome duplicates.
 * Uses start/end word boundaries so a later "12" is not dropped just because
 * "12" already appeared earlier in the line.
 */
export function mergeDictationUtterance(existing: string, next: string): string {
  const a = normalizeUtterance(existing);
  const b = normalizeUtterance(next);
  if (!b) return a;
  if (!a) return b;
  if (a === b) return a;
  if (a.endsWith(b)) return a;
  if (b.endsWith(a)) return b;
  if (b.startsWith(a) && (b.length === a.length || b[a.length] === " ")) return b;
  if (a.startsWith(b) && (a.length === b.length || a[b.length] === " ")) return a;
  return `${a} ${b}`;
}

/** Collapse a whole transcript that is the same phrase repeated 2–4 times. */
export function collapseRepeatedTranscript(text: string): string {
  const t = normalizeUtterance(text);
  if (!t) return t;
  const words = t.split(" ");
  for (const n of [3, 2, 4]) {
    if (words.length < n || words.length % n !== 0) continue;
    const size = words.length / n;
    const chunk = words.slice(0, size).join(" ");
    let ok = true;
    for (let i = 1; i < n; i++) {
      if (words.slice(i * size, (i + 1) * size).join(" ") !== chunk) {
        ok = false;
        break;
      }
    }
    if (ok) return chunk;
  }
  return t;
}

export function foldSpeechRecognitionResults(
  results: ReadonlyArray<{ isFinal: boolean; transcript: string }>,
): { finals: string; interim: string } {
  let finals = "";
  let interim = "";
  for (const res of results) {
    const text = normalizeUtterance(res.transcript ?? "");
    if (!text) continue;
    if (res.isFinal) finals = mergeDictationUtterance(finals, text);
    else interim = mergeDictationUtterance(interim, text);
  }
  return { finals, interim };
}

export function joinDictationParts(...parts: string[]): string {
  return parts
    .map((p) => normalizeUtterance(p))
    .filter((p) => p.length > 0)
    .join(" ");
}

/** Commit a new snapshot onto existing pad text without triplicating. */
export function joinTranscriptChunks(existing: string, captured: string): string {
  return collapseRepeatedTranscript(
    mergeDictationUtterance(existing, collapseRepeatedTranscript(captured)),
  );
}

/** Live draft: session finals + this instance's finals + current interim. */
export function liveDictationDraft(
  sessionFinal: string,
  instanceFinals: string,
  interim: string,
): string {
  return collapseRepeatedTranscript(
    mergeDictationUtterance(mergeDictationUtterance(sessionFinal, instanceFinals), interim),
  );
}
